'use client'

import { captureException, setUser } from '@sentry/nextjs'
import { notification } from 'lib/notification'
import { isGatewayError, swrFetcher } from 'lib/swrFetcher'
import { FeatureFlagsContext } from 'lib/useFeatureFlag'
import { MeilisearchApiKeyContext } from 'lib/useSearchClient'
import { useRouter } from 'next/navigation'
import { type ReactNode, useMemo } from 'react'
import { SWRConfig, type SWRConfiguration } from 'swr'
import { AppPathContext } from './AppLink'

interface Props {
  children: ReactNode
  userEmail?: string | null
  appPath?: string
  featureFlags: Record<string, boolean>
  meilisearchApiKey: string | null
}

export function ClientProviders({
  children,
  userEmail,
  appPath,
  featureFlags,
  meilisearchApiKey,
}: Props) {
  const router = useRouter()
  if (userEmail) setUser({ email: userEmail })

  const swrConfig = useMemo(
    (): SWRConfiguration => ({
      fetcher: swrFetcher,
      onError: handleSWRError(router),
    }),
    [router],
  )

  return (
    <SWRConfig value={swrConfig}>
      <AppPathContext value={appPath ?? ''}>
        <FeatureFlagsContext value={featureFlags}>
          <MeilisearchApiKeyContext value={meilisearchApiKey ?? undefined}>
            {children}
          </MeilisearchApiKeyContext>
        </FeatureFlagsContext>
      </AppPathContext>
    </SWRConfig>
  )
}

function handleSWRError(router: { push: (path: string) => void }) {
  return function (err: any) {
    if (err.status === 401) {
      router.push('/api/auth/signin')
      return
    }
    if (
      isGatewayError(err) ||
      err.message.startsWith('NetworkError') ||
      err.message.startsWith('Failed to fetch') ||
      err.message.startsWith('Load failed')
    ) {
      console.error('Network or gateway error', err.message)
      return
    }

    console.error('SWR ERROR:', err)
    captureException(err)
    notification.error({
      title: 'Etwas ist schief gelaufen',
      body: 'Lade die Seite neu.',
    })
  }
}
